import { Button } from '@/components/ui/button'
import { helpEmail } from '@/data/texas'
import { isChunkLoadError } from '@/lib/lazyImport'
import { isRouteErrorResponse, useRouteError } from 'react-router'

/**
 * Top-level `errorElement` for the router. Renders a friendly, actionable
 * page instead of React Router's default "Unexpected Application Error!"
 * screen.
 *
 * Special-cases stale-chunk errors (a new version was deployed while this
 * tab was open) with reassuring, upgrade-specific copy — this is a fallback
 * safety net for the cases `lazyImport` doesn't already recover from
 * automatically (e.g. a second failure in a row).
 */
export function RootErrorBoundary() {
  const error = useRouteError()
  const chunkLoadError = isChunkLoadError(error)

  console.error('Unhandled route error:', error)

  return (
    <div className="mx-auto flex h-svh max-w-lg flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="text-2xl font-semibold">
        {chunkLoadError ? 'Update Available' : 'Something Went Wrong'}
      </h1>
      <p className="text-muted-foreground">
        {chunkLoadError
          ? 'A new version of MCCS was released while you had this page open. Please refresh to continue — any work you already submitted has been saved.'
          : 'An unexpected error occurred. Please try refreshing the page. If the problem continues, contact the helpdesk.'}
      </p>
      <div className="flex gap-2">
        <Button onClick={() => window.location.reload()}>Refresh Page</Button>
        <Button variant="outline" onClick={() => (window.location.href = '/')}>
          Go to Home
        </Button>
      </div>
      {!chunkLoadError && (
        <p className="text-muted-foreground text-sm">
          Need help? Email{' '}
          <a className="underline" href={`mailto:${helpEmail}`}>
            {helpEmail}
          </a>
        </p>
      )}
      {import.meta.env.DEV && (
        <details className="text-muted-foreground mt-4 w-full text-left text-xs">
          <summary>Error details (dev only)</summary>
          <pre className="overflow-auto whitespace-pre-wrap">
            {isRouteErrorResponse(error)
              ? `${error.status} ${error.statusText}`
              : error instanceof Error
                ? (error.stack ?? error.message)
                : String(error)}
          </pre>
        </details>
      )}
    </div>
  )
}

export default RootErrorBoundary
